import React, { useEffect, useState, useRef } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, TablePagination, Snackbar, Alert,
  Select, MenuItem, FormControl, InputLabel, Typography
} from "@mui/material";
import { Add, Edit, Delete, Undo } from "@mui/icons-material";
import { axiosInstances } from "./apiConfig";



export default function UserUI() {

  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const deleteTimers = useRef({});
  const [pendingDeletes, setPendingDeletes] = useState({});
  const [file, setFile] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [total, setTotal] = useState(0);

  const loadUsers = async (pageNum = page, size = rowsPerPage) => {
    try {
      const res = await axiosInstances.userApi.get(`/users?page=${pageNum}&size=${size}`);
      console.log("API Response:", res.data); // Debug line
      const result = res.data;
      const payload = result.data || {};
      setUsers(payload.data || []);
      setTotal(payload.total || 0);
    } catch (err) {
      console.error("Error fetching users", err);
    }
  };

  useEffect(() => { loadUsers(); }, [page, rowsPerPage]);

  const handleOpen = (user = {}) => {
    setForm(user);
    setEditing(!!user.id);
    setOpen(true);
  };

  const handleClose = () => { setForm({}); setOpen(false); setFile(null);};

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };


  const handleSave = async () => {
    try {
      if (file) {
        // Upload Excel file
        const excelFormData = new FormData();
        excelFormData.append("file", file);

        await axiosInstances.userApi.post(`/uploadUsers`, excelFormData);
        setSnackbar({ open: true, message: "Files uploaded successfully!", severity: "success" });
      } else {
        const userPayload = { ...form }; // Assuming 'form' matches UserReq structure

        if (editing) {
          // Update user
          await axiosInstances.userApi.post(`/user/update`, userPayload, {
            headers: { "Content-Type": "application/json" },
          });
          setSnackbar({ open: true, message: "User updated successfully!", severity: "success" });
        } else {
          // Create user
          await axiosInstances.userApi.post(`/users`, userPayload, {
            headers: { "Content-Type": "application/json" },
          });
          setSnackbar({ open: true, message: "User created successfully!", severity: "success" });
        }
      }

      loadUsers();
      handleClose();
      setFile(null);
    } catch (err) {
      if (err.response && err.response.status === 409) {
        setSnackbar({ open: true, message: "Username or Email already exists", severity: "error" });
      } else {
        setSnackbar({ open: true, message: "Something went wrong!", severity: "error" });
      }
    }
  };

  const handleDeleteClick = (id) => {
    setPendingDeletes((prev) => ({ ...prev, [id]: true }));
    deleteTimers.current[id] = setTimeout(async () => {
      try {
        await axiosInstances.userApi.post(`/user/delete`, { id });
        loadUsers();
      } catch (err) {
        console.error("Delete failed", err);
      } finally {
        setPendingDeletes((prev) => {
          const updated = { ...prev };
          delete updated[id];
          return updated;
        });
        deleteTimers.current[id] = null;
      }
    }, 30000);
  };

  const handleUndoClick = (id) => {
    if (deleteTimers.current[id]) {
      clearTimeout(deleteTimers.current[id]);
      deleteTimers.current[id] = null;
    }
    setPendingDeletes((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>User Management</Typography>
      <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => handleOpen()}>
        Add User
      </Button>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sr. No.</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Full Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>User Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user, index) => (
              <TableRow key={user.id}>
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell>{user.id}</TableCell>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.fullName}</TableCell>
                <TableCell>{user.phoneNumber}</TableCell>
                <TableCell>{user.role}</TableCell>
                <TableCell>{user.usr_sts}</TableCell>
                <TableCell>
                  <IconButton color="primary" onClick={() => handleOpen(user)}>
                    <Edit />
                  </IconButton>
                  {pendingDeletes[user.id] ? (
                    <IconButton color="warning" onClick={() => handleUndoClick(user.id)}>
                      <Undo />
                    </IconButton>
                  ) : (
                    <IconButton color="error" onClick={() => handleDeleteClick(user.id)}>
                      <Delete />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">No users found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelDisplayedRows={({ page }) => `Page ${page + 1} of ${Math.ceil(total / rowsPerPage)}`}
        />
      </TableContainer>

      {/* Dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editing ? "Edit User" : "Add User"}</DialogTitle>
        <DialogContent>
          {editing && (
            <TextField fullWidth margin="dense" label="User ID" value={form.id || ""} disabled />
          )}
          <TextField fullWidth margin="dense" label="Username" name="username" value={form.username || ""} onChange={handleChange} />
          <TextField fullWidth margin="dense" label="Email" name="email" value={form.email || ""} onChange={handleChange} />
          {!editing && (
            <TextField fullWidth margin="dense" label="Password" name="password" type="password" value={form.password || ""} onChange={handleChange} />
          )}
          <TextField fullWidth margin="dense" label="Full Name" name="fullName" value={form.fullName || ""} onChange={handleChange} />
          <TextField fullWidth margin="dense" label="Phone Number" name="phoneNumber" value={form.phoneNumber || ""} onChange={handleChange} />
          <FormControl fullWidth margin="dense">
            <InputLabel>Role</InputLabel>
            <Select name="role" value={form.role || ""} onChange={handleChange}>
              <MenuItem value="USER">User</MenuItem>
              <MenuItem value="ADMIN">Admin</MenuItem>
              <MenuItem value="MODERATOR">Moderator</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>UserStatus</InputLabel>
            <Select name="usr_sts" value={form.usr_sts || ""} onChange={handleChange}>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
              <MenuItem value="SUSPENDED">Suspended</MenuItem>
            </Select>
          </FormControl>
          {!editing && (
            <>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ mt: 2 }}
              >
                Upload File
                <input
                  type="file"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {file && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {file.name}
                </Typography>
              )}
            </>
          )}

        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editing ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
